import { useMemo, useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, ExternalLink, GitMerge } from 'lucide-react';
import { Badge, Button, Card, ErrorState, NativeSelect, PageHeader, Skeleton, buttonVariants } from '@mkcore/ui';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { EntityIcon, KindBadge, MergeEntityDialog } from '@/features/knowledge/graph';
import { entityQuery, neighboursQuery } from '@/features/knowledge/queries';
import { KindIcon, SpaceLabel } from '@/features/knowledge/ui';

export const Route = createFileRoute('/_app/knowledge/entities/$id')({ component: EntityPage });

type Source = { type: string; id: string; title: string | null } | null;

/**
 * One entity of the knowledge graph (CO-1136, CO-1144, CO-1140, CO-1137): its names, addresses and record in the app,
 * its observations per category, its relations both ways, where it comes from, its neighbourhood to a depth, and
 * merging it into another entity.
 */
function EntityPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const canUpdate = useCan('knowledge:update');
  const [merging, setMerging] = useState(false);
  const q = useQuery(entityQuery(id));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const e = q.data;
  const categories = [...new Set(e.observations.map((o) => o.category))];
  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/knowledge/entities" className="hover:underline">
            {t('knowledge.graph.title')}
          </Link>
        }
        title={
          <span className="inline-flex items-center gap-2">
            <EntityIcon kind={e.kind} className="size-5 [&>svg]:size-5" />
            {e.name}
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {e.app?.url && (
              <a href={e.app.url} className={buttonVariants({ variant: 'secondary' })}>
                <ExternalLink /> {t(`knowledge.graph.app.${e.app.type}`)}
              </a>
            )}
            {e.itemId && (
              <Link to="/knowledge/items/$id" params={{ id: e.itemId }} className={buttonVariants({ variant: 'secondary' })}>
                <KindIcon kind="item" /> {t('knowledge.graph.openItem')}
              </Link>
            )}
            {canUpdate && !e.mergedInto && !e.itemId && (
              <Button variant="ghost" onClick={() => setMerging(true)}>
                <GitMerge /> {t('knowledge.graph.merge')}
              </Button>
            )}
          </div>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <KindBadge kind={e.kind} />
        <SpaceLabel space={e.space} />
        {e.mergedInto && (
          <Link to="/knowledge/entities/$id" params={{ id: e.mergedInto }} className="text-sm underline">
            {t('knowledge.graph.mergedInto')}
          </Link>
        )}
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-4">
          <Card role="region" aria-label={t('knowledge.graph.observations')} className="grid gap-3 p-4">
            <h2 className="text-sm font-bold">{t('knowledge.graph.observations')}</h2>
            {!categories.length && <p className="text-sm text-fg-3">{t('knowledge.graph.noObservations')}</p>}
            {categories.map((c) => (
              <div key={c} className="grid gap-1">
                <h3 className="text-xs font-bold uppercase tracking-wide text-fg-3">{c}</h3>
                <ul className="grid gap-1 text-sm">
                  {e.observations
                    .filter((o) => o.category === c)
                    .map((o) => (
                      <li key={o.id} className="flex flex-wrap items-baseline gap-1.5">
                        <span>{o.content}</span>
                        {o.tags.map((tag) => (
                          <Badge key={tag} tone="outline">
                            #{tag}
                          </Badge>
                        ))}
                        <SourceLink source={o.source} />
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </Card>
          <Card role="region" aria-label={t('knowledge.graph.relations')} className="grid gap-2 p-4">
            <h2 className="text-sm font-bold">{t('knowledge.graph.relations')}</h2>
            {!e.relations.length && <p className="text-sm text-fg-3">{t('knowledge.graph.noRelations')}</p>}
            <ul className="grid gap-1 text-sm">
              {e.relations.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-1.5">
                  {r.direction === 'out' ? <ArrowRight className="size-3.5 text-fg-3" aria-label={t('knowledge.graph.out')} /> : <ArrowLeft className="size-3.5 text-fg-3" aria-label={t('knowledge.graph.in')} />}
                  <span className="text-fg-2">{r.type.replace(/_/g, ' ')}</span>
                  <EntityIcon kind={r.other.kind} />
                  {r.other.id ? (
                    <Link to="/knowledge/entities/$id" params={{ id: r.other.id }} className="hover:underline">
                      {r.other.name}
                    </Link>
                  ) : (
                    <span className="text-fg-3" title={t('knowledge.graph.openRelation')}>
                      {r.other.name}
                    </span>
                  )}
                  <SourceLink source={r.source} />
                </li>
              ))}
            </ul>
          </Card>
          <Neighbours id={e.id} />
        </div>
        <div className="grid content-start gap-4">
          <Card role="region" aria-label={t('knowledge.item.details')} className="p-4">
            <dl className="grid gap-1.5 text-sm">
              {!!e.aliases.length && <Row label={t('knowledge.graph.aliases')}>{e.aliases.join(', ')}</Row>}
              {!!e.emails.length && <Row label={t('knowledge.graph.emails')}>{e.emails.join(', ')}</Row>}
              {Object.entries(e.attributes).map(([k, v]) => (
                <Row key={k} label={k}>
                  {Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </Row>
              ))}
              <Row label={t('knowledge.item.fields.updated')}>{formatDateTime(e.updatedAt)}</Row>
            </dl>
          </Card>
          {!!e.sources.length && (
            <Card role="region" aria-label={t('knowledge.graph.sources')} className="grid gap-2 p-4">
              <h2 className="text-sm font-bold">{t('knowledge.graph.sources')}</h2>
              <ul className="grid gap-1 text-sm">
                {e.sources.map((s) => (
                  <li key={`${s.type}:${s.id}`} className="flex items-center gap-1.5">
                    <SourceLink source={s} full />
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
      {merging && <MergeEntityDialog entity={e} onClose={() => setMerging(false)} onMerged={(into) => void navigate({ to: '/knowledge/entities/$id', params: { id: into } })} />}
    </>
  );
}

/** Where a fact comes from: the item or document, as a small link. */
function SourceLink({ source, full }: { source: Source; full?: boolean }) {
  const { t } = useTranslation();
  if (!source) return null;
  const label = source.title ?? t('knowledge.graph.source');
  const cls = full ? 'hover:underline' : 'text-xs text-fg-3 hover:underline';
  if (source.type === 'item')
    return (
      <Link to="/knowledge/items/$id" params={{ id: source.id }} className={cls}>
        {full && <KindIcon kind="item" className="mr-1 inline-flex align-text-bottom" />}
        {label}
      </Link>
    );
  if (source.type === 'document')
    return (
      <Link to="/knowledge/documents/$id" params={{ id: source.id }} className={cls}>
        {full && <KindIcon kind="document" className="mr-1 inline-flex align-text-bottom" />}
        {label}
      </Link>
    );
  return <span className={cls}>{label}</span>;
}

/** The neighbourhood to a depth, nearest first (CO-1140). */
function Neighbours({ id }: { id: string }) {
  const { t } = useTranslation();
  const [depth, setDepth] = useState(1);
  const q = useQuery(neighboursQuery(id, depth));
  const byDepth = useMemo(() => {
    const out = new Map<number, NonNullable<typeof q.data>['nodes']>();
    for (const n of q.data?.nodes ?? []) if (n.depth > 0) out.set(n.depth, [...(out.get(n.depth) ?? []), n]);
    return [...out.entries()].sort((a, b) => a[0] - b[0]);
  }, [q.data]);
  return (
    <Card role="region" aria-label={t('knowledge.graph.neighbours')} className="grid gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold">{t('knowledge.graph.neighbours')}</h2>
        <label className="flex items-center gap-2 text-sm">
          {t('knowledge.graph.depth')}
          <NativeSelect value={String(depth)} onChange={(e) => setDepth(Number(e.target.value))} className="w-20">
            {[1, 2, 3].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </NativeSelect>
        </label>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-24" />
      ) : !byDepth.length ? (
        <p className="text-sm text-fg-3">{t('knowledge.graph.noNeighbours')}</p>
      ) : (
        byDepth.map(([d, nodes]) => (
          <div key={d} className="grid gap-1">
            <h3 className="text-xs font-bold uppercase tracking-wide text-fg-3">{t('knowledge.graph.step', { count: d })}</h3>
            <ul className="flex flex-wrap gap-2 text-sm">
              {nodes.map((n) => (
                <li key={n.id}>
                  <Link to="/knowledge/entities/$id" params={{ id: n.id }} className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 hover:bg-surface-alt">
                    <EntityIcon kind={n.kind} />
                    {n.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
      {q.data?.truncated && <p className="text-xs text-fg-3">{t('knowledge.graph.truncated')}</p>}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2">
      <dt className="text-fg-3">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
